import React, { DragEvent, useRef, useState } from 'react';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { Alert, Box, Button, IconButton, Stack, Typography } from '@mui/material';

const MAX_FILES = 10;
const MAX_BYTES = 20 * 1024 * 1024;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes % (1024 * 1024) === 0 ? 0 : 1)} MB`;
}

function mimeType(file: File): string {
  return file.type || 'application/octet-stream';
}

function sameFile(left: File, right: File): boolean {
  return left.name === right.name && left.size === right.size && left.lastModified === right.lastModified;
}

export interface FeedbackAttachmentPickerProps {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
}

/** Type-agnostic, client-side validation for navbar-feedback attachments only. */
export default function FeedbackAttachmentPicker({
  files,
  onChange,
  disabled = false,
}: FeedbackAttachmentPickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');
  const totalBytes = files.reduce((total, file) => total + file.size, 0);

  const addFiles = (candidateFiles: File[]) => {
    if (disabled || candidateFiles.length === 0) return;
    const next = [...files];
    for (const file of candidateFiles) {
      if (next.some((existing) => sameFile(existing, file))) continue;
      if (file.size > MAX_BYTES) {
        setError(`${file.name} is larger than 20 MB.`);
        continue;
      }
      if (next.length >= MAX_FILES) {
        setError('You can attach up to 10 files.');
        continue;
      }
      if (next.reduce((total, existing) => total + existing.size, 0) + file.size > MAX_BYTES) {
        setError('Attachments must total 20 MB or less.');
        continue;
      }
      next.push(file);
    }
    onChange(next);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    if (!disabled) addFiles(Array.from(event.dataTransfer.files || []));
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        data-testid="feedback-attachment-drop-zone"
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label="Attach feedback files"
        aria-disabled={disabled}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(event) => {
          if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        sx={(theme) => ({
          width: '100%',
          boxSizing: 'border-box',
          px: 2,
          py: 1.5,
          border: '1px dashed',
          borderColor: dragging ? 'primary.main' : 'divider',
          borderRadius: 1.5,
          bgcolor: dragging ? 'action.hover' : 'transparent',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.55 : 1,
          outline: 'none',
          '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
        })}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <AttachFileIcon color={dragging ? 'primary' : 'action'} aria-hidden="true" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600}>
              {dragging ? 'Drop files to attach' : 'Drop files here or browse'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Upload screenshots or other files to help us understand your feedback. Up to 10 files, 20 MB total.
            </Typography>
          </Box>
          <Button
            component="span"
            size="small"
            disabled={disabled}
            onClick={(event) => {
              event.stopPropagation();
              inputRef.current?.click();
            }}
          >
            Browse files
          </Button>
        </Stack>
        <input
          ref={inputRef}
          aria-label="Browse files"
          type="file"
          multiple
          disabled={disabled}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => {
            addFiles(Array.from(event.target.files || []));
            event.currentTarget.value = '';
          }}
          style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
        />
      </Box>
      {error && <Alert severity="error" role="alert" sx={{ mt: 1 }}>{error}</Alert>}
      {files.length > 0 && (
        <Box sx={{ mt: 1 }} aria-label="Selected attachments">
          <Typography variant="caption" color="text.secondary">
            Attachments ({files.length}/10) · {formatBytes(totalBytes)} / 20 MB
          </Typography>
          <Stack spacing={0.25} sx={{ mt: 0.5 }}>
            {files.map((file) => (
              <Stack key={`${file.name}-${file.size}-${file.lastModified}`} direction="row" spacing={0.75} alignItems="center">
                <InsertDriveFileOutlinedIcon fontSize="small" color="action" aria-hidden="true" />
                <Typography variant="body2" noWrap title={file.name} sx={{ flex: 1, minWidth: 0 }}>
                  {file.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {formatBytes(file.size)} · {mimeType(file)}
                </Typography>
                <IconButton
                  size="small"
                  aria-label={`Remove ${file.name}`}
                  disabled={disabled}
                  onClick={() => onChange(files.filter((candidate) => candidate !== file))}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
